import React, { forwardRef, LiHTMLAttributes, useId } from 'react';
import { buildCSSWithLength, CSSValueWithLength, getAutoColorCode } from '../../styles';
import styled from '@emotion/styled';
import { semantic_colors, shapes, text_styles } from '../../foundation';
import { IconArrowChevronRight } from '../icons/generated';

export interface DropdownCompositeItemProps extends LiHTMLAttributes<HTMLLIElement> {
  disabled?: boolean;
  /**
   *  @default false
   *  Option 선택 여부
   */
  selected?: boolean;
  /**
   *  @default false
   *  Option focus 여부
   */
  focused?: boolean;
  width?: CSSValueWithLength;
  optionItemHeight?: number;
}

export const DropdownCompositeItem = forwardRef<HTMLLIElement, DropdownCompositeItemProps>(
  ({ children, disabled = false, selected = false, width = 164, optionItemHeight, ...props }, ref) => {
    const id = useId();

    return (
      <SC.DropdownCompositeItem
        width={width}
        disabled={disabled}
        selected={selected}
        height={optionItemHeight}
        role='option'
        id={`pds-dropdown-composite-item-${id}`}
        {...props}
        ref={ref}
      >
        {children}
        <SC.Icon color={selected ? 'currentColor' : semantic_colors.content.quaternary} />
      </SC.DropdownCompositeItem>
    );
  },
);

const SC = {
  DropdownCompositeItem: styled.li<{
    disabled?: boolean;
    selected?: boolean;
    focused?: boolean;
    width?: CSSValueWithLength;
    height?: number;
  }>`
    display: flex;
    align-items: center;
    justify-content: space-between;
    ${({ height }) => buildCSSWithLength('height', height)};
    ${({ width }) => buildCSSWithLength('width', width)};
    padding: 8px 8px 8px 12px;
    ${text_styles.Body_13_Regular};
    cursor: pointer;
    ${shapes.border_radius.xsmall};
    background-color: ${semantic_colors.background.surface};
    color: ${semantic_colors.content.primary};
    outline: none;
    transition: all 0.1s linear;

    &:hover {
      background-color: ${getAutoColorCode(semantic_colors.background.surface).hover};
    }
    &:active {
      background-color: ${getAutoColorCode(semantic_colors.background.surface).pressed};
    }
    ${({ disabled }) =>
      disabled &&
      `
        cursor: not-allowed;
        opacity: 0.4;
      `}
    ${({ selected }) =>
      selected &&
      `
        background-color: ${semantic_colors.accent.light};
        color: ${semantic_colors.accent.primary};
        &:hover {
          background-color: ${getAutoColorCode(semantic_colors.accent.light).hover};
        }
        &:active {
          background-color: ${getAutoColorCode(semantic_colors.accent.light).pressed};
        }
      `}
  `,
  Icon: styled(IconArrowChevronRight)`
    flex-shrink: 0;
  `,
};
